import { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Mail, Lock, Eye, EyeOff, Loader2, X, CheckCircle, AlertCircle, XCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LoginPage = () => {
  const { login } = useAuth();
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [settings, setSettings] = useState(null);
  const [showForgotModal, setShowForgotModal] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotSuccess, setForgotSuccess] = useState(false);
  const [forgotError, setForgotError] = useState('');
  const [verifyStatus, setVerifyStatus] = useState(null);
  const [verifyMessage, setVerifyMessage] = useState('');
  const [recaptchaToken, setRecaptchaToken] = useState('');
  const [toast, setToast] = useState(null);
  const recaptchaRef = useRef(null);
  const recaptchaWidgetId = useRef(null);

  // Tampilkan toast + auto-dismiss setelah 4 detik (konsisten dengan App.js)
  const showToast = useCallback((message, type = 'error') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const verifyToken = params.get('token');
    const isVerifyPage = window.location.pathname === '/verify-email';
    if (isVerifyPage && verifyToken) {
      setVerifyStatus('loading');
      axios.post(`${API}/auth/verify-email`, { token: verifyToken })
        .then(res => { setVerifyStatus('success'); setVerifyMessage(res.data.message || 'Email berhasil diverifikasi!'); })
        .catch(err => { setVerifyStatus('error'); setVerifyMessage(err.response?.data?.detail || 'Token tidak valid.'); });
    }
  }, []);

  useEffect(() => {
    axios.get(`${API}/settings`).then(res => setSettings(res.data)).catch(() => {});
  }, []);

  // Load reCAPTCHA script when settings are loaded
  const renderRecaptcha = useCallback(() => {
    if (!settings?.recaptchaEnabled || !settings?.recaptchaSiteKey) return;
    if (!recaptchaRef.current) return;
    if (recaptchaWidgetId.current !== null) return;

    const version = settings.recaptchaVersion || 'v2';

    if (version === 'v2' && window.grecaptcha && window.grecaptcha.render) {
      try {
        recaptchaWidgetId.current = window.grecaptcha.render(recaptchaRef.current, {
          sitekey: settings.recaptchaSiteKey,
          callback: (token) => setRecaptchaToken(token),
          'expired-callback': () => setRecaptchaToken(''),
        });
      } catch (e) {
        // already rendered
      }
    }
  }, [settings]);

  useEffect(() => {
    if (!settings?.recaptchaEnabled || !settings?.recaptchaSiteKey) return;
    
    const version = settings.recaptchaVersion || 'v2';

    // Check if script already loaded
    if (document.querySelector('script[src*="recaptcha"]')) {
      if (window.grecaptcha && window.grecaptcha.render) {
        renderRecaptcha();
      } else {
        window.onRecaptchaLoad = renderRecaptcha;
      }
      return;
    }

    const scriptUrl = version === 'v3'
      ? `https://www.google.com/recaptcha/api.js?render=${settings.recaptchaSiteKey}`
      : 'https://www.google.com/recaptcha/api.js?onload=onRecaptchaLoad&render=explicit';

    window.onRecaptchaLoad = renderRecaptcha;

    const script = document.createElement('script');
    script.src = scriptUrl;
    script.async = true;
    script.defer = true;
    document.head.appendChild(script);

    return () => {
      delete window.onRecaptchaLoad;
    };
  }, [settings, renderRecaptcha]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    const recaptchaEnabled = settings?.recaptchaEnabled && settings?.recaptchaSiteKey;
    const version = settings?.recaptchaVersion || 'v2';

    try {
      let token = recaptchaToken;

      // For V3, get token via execute
      if (recaptchaEnabled && version === 'v3' && window.grecaptcha) {
        token = await window.grecaptcha.execute(settings.recaptchaSiteKey, { action: 'login' });
      }

      // For V2, validate checkbox was checked
      if (recaptchaEnabled && version === 'v2' && !token) {
        const msg = 'Silakan centang reCAPTCHA terlebih dahulu.';
        setError(msg);
        showToast(msg, 'error');
        setLoading(false);
        return;
      }

      // login() returns {success, error} — TIDAK throw, jadi harus dicek manual
      const result = await login(formData.email, formData.password, token);
      if (!result?.success) {
        const msg = result?.error || 'Email atau password salah. Silakan coba lagi.';
        setError(msg);
        showToast(msg, 'error');
        // Reset V2 reCAPTCHA setelah gagal login
        if (recaptchaEnabled && version === 'v2' && window.grecaptcha && recaptchaWidgetId.current !== null) {
          window.grecaptcha.reset(recaptchaWidgetId.current);
          setRecaptchaToken('');
        }
      }
    } catch (err) {
      // Fallback jaga-jaga kalau nanti ada error yang di-throw
      const msg = err.response?.data?.detail || err.message || 'Login gagal. Periksa koneksi Anda.';
      setError(msg);
      showToast(msg, 'error');
      if (recaptchaEnabled && version === 'v2' && window.grecaptcha && recaptchaWidgetId.current !== null) {
        window.grecaptcha.reset(recaptchaWidgetId.current);
        setRecaptchaToken('');
      }
    }
    setLoading(false);
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setForgotLoading(true);
    setForgotError('');
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: forgotEmail });
      setForgotSuccess(true);
    } catch (error) {
      setForgotError('Gagal mengirim email. Silakan coba lagi.');
    }
    setForgotLoading(false);
  };

  const closeForgotModal = () => {
    setShowForgotModal(false);
    setForgotEmail('');
    setForgotError('');
    setForgotSuccess(false);
  };

  const appName = settings?.appName || 'InvoicePro';
  const companyName = settings?.companyName || 'PT. Tren Masa Kini';
  const logoImage = settings?.logoImage;

  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-4" style={{background: '#E8EDF6'}}>

      {/* Logo */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="mb-8"
      >
        {logoImage ? (
          <img src={logoImage} alt={appName} className="h-14 object-contain" data-testid="login-logo-image" />
        ) : (
          <h1 className="text-2xl font-heading font-bold" style={{color: '#212529'}}>{companyName}</h1>
        )}
      </motion.div>

      {/* Login Card */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.1 }}
        className="w-full max-w-md bg-white rounded-2xl p-8"
        style={{boxShadow: '0 4px 30px rgba(0,0,0,0.08)'}}
      >
        <h2 className="text-2xl font-heading font-bold text-center mb-8" style={{color: '#212529'}} data-testid="login-app-name">
          Sign In
        </h2>

        {/* Verification Banner */}
        {verifyStatus && (
          <div className={`mb-5 p-4 rounded-xl flex items-start gap-3 ${
            verifyStatus === 'loading' ? 'bg-blue-50 border border-blue-200' :
            verifyStatus === 'success' ? 'bg-emerald-50 border border-emerald-200' :
            'border'
          }`} style={verifyStatus === 'error' ? {background: '#F6294710', borderColor: '#F6294730'} : {}}
          data-testid="verify-email-banner">
            {verifyStatus === 'loading' && <><Loader2 className="w-5 h-5 animate-spin flex-shrink-0 mt-0.5" style={{color: '#0091A0'}} /><p className="text-sm" style={{color: '#0091A0'}}>Memverifikasi email...</p></>}
            {verifyStatus === 'success' && <><CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" /><p className="text-sm text-emerald-700">{verifyMessage}</p></>}
            {verifyStatus === 'error' && <><AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" style={{color: '#F62947'}} /><p className="text-sm" style={{color: '#F62947'}}>{verifyMessage}</p></>}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Email */}
          <div>
            <label className="block text-sm font-medium mb-2" style={{color: '#212529'}}>
              Email:<span style={{color: '#F62947'}}>*</span>
            </label>
            <div className="relative">
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="email@example.com"
                className="w-full px-4 py-3 border rounded-lg text-sm transition-all focus:outline-none focus:ring-2"
                style={{background: '#FFFFFF', borderColor: '#CED4DA', color: '#212529', '--tw-ring-color': '#00A6B7'}}
                required
                data-testid="login-email-input"
              />
              <Mail className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4" style={{color: '#ADB5BD'}} />
            </div>
          </div>

          {/* Password */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium" style={{color: '#212529'}}>
                Password:<span style={{color: '#F62947'}}>*</span>
              </label>
              <button
                type="button"
                onClick={() => setShowForgotModal(true)}
                className="text-sm font-medium transition-colors hover:underline"
                style={{color: '#0091A0'}}
                data-testid="forgot-password-btn"
              >
                Forgot your password?
              </button>
            </div>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                name="password"
                value={formData.password}
                onChange={handleChange}
                placeholder="••••••••"
                className="w-full px-4 py-3 border rounded-lg text-sm transition-all focus:outline-none focus:ring-2"
                style={{background: '#FFFFFF', borderColor: '#CED4DA', color: '#212529', '--tw-ring-color': '#00A6B7'}}
                required
                data-testid="login-password-input"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-4 top-1/2 -translate-y-1/2 transition-colors"
                style={{color: '#ADB5BD'}}
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          {/* Error */}
          {error && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="p-3 rounded-lg text-sm"
              style={{background: '#F6294710', border: '1px solid #F6294730', color: '#F62947'}}
              data-testid="auth-error-message"
            >
              {error}
            </motion.div>
          )}

          {/* Google reCAPTCHA V2 widget */}
          {settings?.recaptchaEnabled && settings?.recaptchaSiteKey && (settings?.recaptchaVersion || 'v2') === 'v2' && (
            <div className="flex justify-center" data-testid="recaptcha-container">
              <div ref={recaptchaRef}></div>
            </div>
          )}

          {/* Login Button */}
          <button
            type="submit"
            disabled={loading}
            className="w-full py-3.5 text-white font-semibold rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            style={{background: '#00A6B7'}}
            data-testid="login-submit-btn"
          >
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Login'}
          </button>
        </form>
      </motion.div>

      {/* Footer */}
      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.5 }}
        className="mt-8 text-sm"
        style={{color: '#6C757D'}}
      >
        All rights reserved &copy; {new Date().getFullYear()}{' '}
        <span className="font-medium" style={{color: '#0091A0'}}>{companyName}</span>
      </motion.p>

      {/* Forgot Password Modal */}
      <AnimatePresence>
        {showForgotModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/30 backdrop-blur-sm flex items-center justify-center p-4 z-50"
            onClick={closeForgotModal}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-md"
            >
              {!forgotSuccess ? (
                <>
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="text-xl font-bold" style={{color: '#212529'}}>Lupa Password</h3>
                    <button onClick={closeForgotModal} className="p-2 rounded-lg transition-colors hover:bg-gray-100" style={{color: '#6C757D'}}>
                      <X size={20} />
                    </button>
                  </div>

                  <p className="text-sm mb-6" style={{color: '#6C757D'}}>
                    Masukkan email Anda yang terdaftar. Kami akan mengirimkan link untuk reset password yang berlaku selama <strong style={{color: '#00A6B7'}}>5 menit</strong>.
                  </p>

                  <form onSubmit={handleForgotPassword} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium mb-2" style={{color: '#212529'}}>Email</label>
                      <input
                        type="email"
                        value={forgotEmail}
                        onChange={(e) => { setForgotEmail(e.target.value); setForgotError(''); }}
                        placeholder="email@example.com"
                        className="w-full px-4 py-3 border rounded-lg text-sm transition-all focus:outline-none focus:ring-2"
                        style={{borderColor: '#CED4DA', color: '#212529', '--tw-ring-color': '#00A6B7'}}
                        required
                        autoFocus
                        data-testid="forgot-email-input"
                      />
                    </div>

                    {forgotError && (
                      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="p-3 rounded-lg text-sm" style={{background: '#F6294710', border: '1px solid #F6294730', color: '#F62947'}}>
                        {forgotError}
                      </motion.div>
                    )}

                    <div className="flex gap-3 pt-2">
                      <button type="button" onClick={closeForgotModal} className="flex-1 py-3 font-medium rounded-lg transition-colors border" style={{borderColor: '#CED4DA', color: '#212529'}}>
                        Batal
                      </button>
                      <button type="submit" disabled={forgotLoading} className="flex-1 py-3 text-white font-semibold rounded-lg transition-all disabled:opacity-50 flex items-center justify-center gap-2" style={{background: '#00A6B7'}} data-testid="send-reset-btn">
                        {forgotLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Kirim Link'}
                      </button>
                    </div>
                  </form>
                </>
              ) : (
                <div className="text-center py-4">
                  <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: 'spring', stiffness: 200 }} className="w-16 h-16 bg-emerald-50 rounded-full flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="w-8 h-8 text-emerald-500" />
                  </motion.div>
                  <h3 className="text-xl font-bold mb-2" style={{color: '#212529'}}>Email Terkirim!</h3>
                  <p className="text-sm mb-6" style={{color: '#6C757D'}}>
                    Link reset password telah dikirim ke <strong style={{color: '#212529'}}>{forgotEmail}</strong>. Silakan cek inbox atau folder spam Anda.
                  </p>
                  <p className="text-xs mb-6" style={{color: '#FFB821'}}>Link berlaku selama 5 menit</p>
                  <button onClick={closeForgotModal} className="w-full py-3 text-white font-semibold rounded-lg" style={{background: '#00A6B7'}}>
                    Kembali ke Login
                  </button>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Toast Notification (posisi & style konsisten dengan aplikasi utama) */}
      <AnimatePresence>
        {toast && (
          <motion.div
            key="login-toast"
            initial={{ opacity: 0, y: 50, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 50, scale: 0.9 }}
            transition={{ duration: 0.22 }}
            className="fixed bottom-6 right-6 z-[100] flex items-center gap-3 px-5 py-4 rounded-xl shadow-lg text-white max-w-md"
            style={{
              backgroundColor:
                toast.type === 'error' ? '#F62947' :
                toast.type === 'success' ? '#10B981' : '#212529',
            }}
            data-testid="login-toast"
          >
            {toast.type === 'success' && <CheckCircle size={20} />}
            {toast.type === 'error' && <XCircle size={20} />}
            {toast.type !== 'success' && toast.type !== 'error' && <AlertCircle size={20} />}
            <span className="font-medium text-sm">{toast.message}</span>
            <button
              onClick={() => setToast(null)}
              className="ml-2 hover:opacity-70 flex-shrink-0"
              data-testid="login-toast-close"
              aria-label="Tutup notifikasi"
            >
              <X size={18} />
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default LoginPage;
